/**
 * Oblique follow camera. Keeps Phase 0's fixed alpha/beta/radius (the
 * Brawl-Stars-ish three-quarter view) but re-targets the character every frame
 * with frame-rate-independent smoothing. The camera is purely visual — it is
 * NOT part of the deterministic sim — and has no user controls attached.
 */
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera.js";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import type { Scene } from "@babylonjs/core/scene.js";

export interface FollowCamera {
  readonly camera: ArcRotateCamera;
  follow(target: Vector3, dt: number): void;
}

export const createFollowCamera = (scene: Scene): FollowCamera => {
  const camera = new ArcRotateCamera(
    "camera",
    -Math.PI / 2,
    Math.PI / 3.4,
    28,
    Vector3.Zero(),
    scene,
  );
  // Deliberately no attachControl: input drives the character, not the camera.

  const smoothed = Vector3.Zero();
  let initialized = false;

  const follow = (target: Vector3, dt: number): void => {
    if (!initialized) {
      smoothed.copyFrom(target);
      initialized = true;
    } else {
      // Exponential smoothing; independent of frame rate.
      const k = 1 - Math.pow(0.0015, dt);
      smoothed.x += (target.x - smoothed.x) * k;
      smoothed.y += (target.y - smoothed.y) * k;
      smoothed.z += (target.z - smoothed.z) * k;
    }
    camera.setTarget(smoothed);
  };

  return { camera, follow };
};
